import _max from 'lodash/max'
import _min from 'lodash/min'
import { IMapRouteGroup } from 'transfix-maps/types'
import { NORTH_AMERICA_BOUNDS } from '../constants'
import { ILatLonPosition, IMapPosition, IMapLane, IMapStopProps } from '../types'
import { getLat } from './getLat'
import { getLon } from './getLon'

/**
 * Transfix HQ in New York
 */
const DEFAULT_INITIAL_POSITION: ILatLonPosition = {
  lat: 40.75269,
  lon: -73.98962,
}

const MAX_LAT = NORTH_AMERICA_BOUNDS[0].lat
const MAX_LON = NORTH_AMERICA_BOUNDS[0].lon
const MIN_LAT = NORTH_AMERICA_BOUNDS[1].lat
const MIN_LON = NORTH_AMERICA_BOUNDS[0].lon

/**
 * Calculates the bounds of the map to fit all stops
 * @deprecated Use getMapFitBounds instead
 */
export const getFitBounds = (
  mapLanes: IMapLane[] = [],
  mapPositions: IMapPosition[] = [],
  mapStops: IMapStopProps[] = [],
  mapLines: IMapRouteGroup[] = [],
  zoomLevel: number = 1
): [[number, number], [number, number]] => {
  let fitBounds: [[number, number], [number, number]] = [
    [NORTH_AMERICA_BOUNDS[0].lon, NORTH_AMERICA_BOUNDS[0].lat],
    [NORTH_AMERICA_BOUNDS[1].lon, NORTH_AMERICA_BOUNDS[1].lat],
  ]

  const positions: ILatLonPosition[] = mapLanes.reduce(
    (acc: ILatLonPosition[], r: IMapLane) => {
      const newCoords = []
      if (r.start) newCoords.push(r.start.coords)
      if (r.stop) newCoords.push(r.stop.coords)
      return acc.concat(newCoords)
    },
    mapPositions.map(p => p.coords)
  )

  // If there are stops
  if (mapStops.length) {
    const longitudes = mapStops.map(getLon)
    const latitudes = mapStops.map(getLat)
    const maxLat = _max(latitudes) ?? MAX_LAT
    const maxLon = _max(longitudes) ?? MAX_LON
    const minLat = _min(latitudes) ?? MIN_LAT
    const minLon = _min(longitudes) ?? MIN_LON

    // set the map the full breadth of the route
    fitBounds = [
      [minLon - zoomLevel, minLat - zoomLevel],
      [maxLon + zoomLevel, maxLat + zoomLevel],
    ]
    // if we only have one position we want to
  } else if (mapLines.length) {
    const latArray = []
    const lonArray = []

    for (const line of mapLines) {
      const { route = [] } = line || {}

      for (const lineRoute of route) {
        const { coords } = lineRoute || {}
        const { lat, lon } = coords || {}
        latArray.push(lat)
        lonArray.push(lon)
      }
    }

    const maxLat = _max(latArray) ?? MAX_LAT
    const maxLon = _max(lonArray) ?? MAX_LON
    const minLat = _min(latArray) ?? MIN_LAT
    const minLon = _min(lonArray) ?? MIN_LON

    // set the map the full breadth of the route
    fitBounds = [
      [minLon - zoomLevel, minLat - zoomLevel],
      [maxLon + zoomLevel, maxLat + zoomLevel],
    ]
  } else if (positions.length < 2) {
    const [firstPosition] = positions
    const mapCenter = firstPosition || DEFAULT_INITIAL_POSITION

    // set the map boundaries to a small square around that location
    fitBounds = [
      [mapCenter.lon - zoomLevel, mapCenter.lat - zoomLevel],
      [mapCenter.lon + zoomLevel, mapCenter.lat + zoomLevel],
    ]
  } else {
    const allLatitudes = positions.map(({ lat }) => lat)
    const allLongitudes = positions.map(({ lon }) => lon)
    const southWest: [number, number] = [Math.min(...allLongitudes), Math.min(...allLatitudes)]
    const northEast: [number, number] = [Math.max(...allLongitudes), Math.max(...allLatitudes)]

    // set the map the full breadth of the route
    fitBounds = [southWest, northEast]
  }

  return fitBounds
}
